import { Component, inject, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { DocumentDynamicsService } from '../../document-dynamics.service';
import { DacGeneralParamsService } from '../../../dac-general-params/dac-general-params.service';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DynaFieldType, DynaFields, DynaFieldsRequest, DynaStatus, ExtractedPlaceholder, ParametrageGeneralDac, TemplatePlaceholders } from '@/types';

interface ReviewRow {
  key: string;
  suggestedName: string;
  suggestedType: 'TEXT' | 'TABLE' | 'LIST';
  matchedGlobalId: number | null;
  include: boolean;
}

/**
 * After a template is uploaded, scans it for "**key**" markers and offers to create the
 * ones that are new: attached as a shared variable when the name matches an existing global
 * one, otherwise created as a local field (TEXT by default, TABLE/LIST when the marker itself
 * says so via a table:/list: prefix) - to be refined afterward via the normal edit action.
 * This is a review step, nothing is created until the user confirms.
 */
@Component({
  selector: 'sm-template-placeholders-review',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, TranslateModule],
  templateUrl: './template-placeholders-review.component.html',
  styleUrls: ['./template-placeholders-review.component.scss'],
})
export class TemplatePlaceholdersReviewComponent {
  @Input() dynaDocsId?: number | null;
  @Input() dynaDocsVer?: number | null;
  // Where new local fields get appended - parent passes its current field count.
  @Input() nextOrderStart = 1;

  // Fired once creation/attachment calls have finished, so the parent can refresh its lists.
  @Output() completed = new EventEmitter<void>();

  private readonly documentDynamicsService = inject(DocumentDynamicsService);
  private readonly dacGeneralParamsService = inject(DacGeneralParamsService);
  private readonly dacRepository = inject(DacRepository);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  protected visible = false;
  protected loading = false;
  protected rows: ReviewRow[] = [];
  protected suspicious: string[] = [];

  // Called explicitly by the parent right after a successful template upload - not tied to
  // any input change, so opening an existing document doesn't re-run this every time.
  runExtraction(templateName: string): void {
    if (!templateName) return;

    this.loading = true;
    const placeholders$: Observable<TemplatePlaceholders> = this.documentDynamicsService
      .extractPlaceholders(templateName)
      .pipe(catchError((): Observable<TemplatePlaceholders> => of({ placeholders: [], suspicious: [] })));
    const library$: Observable<ParametrageGeneralDac[]> = this.dacGeneralParamsService
      .getAllList()
      .pipe(catchError((): Observable<ParametrageGeneralDac[]> => of([])));

    forkJoin([placeholders$, library$])
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result: [TemplatePlaceholders, ParametrageGeneralDac[]]) => {
        const [extraction, globalVariables] = result;
        this.suspicious = extraction.suspicious;
        this.rows = extraction.placeholders.map((p: ExtractedPlaceholder) => {
          const match = globalVariables.find(
            v =>
              (v.paramGeneralName ?? '').toLowerCase() === p.key.toLowerCase() ||
              (v.paramGeneralLabel ?? '').toLowerCase() === p.suggestedName.toLowerCase()
          );
          return {
            key: p.key,
            suggestedName: p.suggestedName,
            suggestedType: p.suggestedType,
            matchedGlobalId: match ? match.id : null,
            include: true,
          };
        });
        this.visible = this.rows.length > 0 || this.suspicious.length > 0;
      });
  }

  protected typeLabel(row: ReviewRow): string {
    if (row.matchedGlobalId) return 'dynamicDocument.globalVariables.badge';
    if (row.suggestedType === 'TABLE') return 'dynamicDocument.form.table';
    if (row.suggestedType === 'LIST') return 'dynamicDocument.form.list';
    return 'dynamicDocument.form.text';
  }

  protected confirm(): void {
    const included = this.rows.filter(r => r.include);
    if (!included.length || !this.dynaDocsId || !this.dynaDocsVer) {
      this.dismiss();
      return;
    }

    const dynaDocsId = this.dynaDocsId;
    const dynaDocsVer = this.dynaDocsVer;

    const calls: Observable<unknown>[] = included.map((row, index) => {
      if (row.matchedGlobalId) {
        return this.dacGeneralParamsService
          .addPlacement({ paramGeneralDacId: row.matchedGlobalId, dynaDocsId, dynaDocsVer })
          .pipe(catchError((): Observable<null> => of(null)));
      }

      const request: DynaFieldsRequest = {
        dynaDocsId,
        name: row.suggestedName,
        ref: '',
        label: row.suggestedName,
        description: '',
        status: DynaStatus.ACTIF,
        type: row.suggestedType === 'TABLE' ? DynaFieldType.TABLE : row.suggestedType === 'LIST' ? DynaFieldType.LIST : DynaFieldType.TEXT,
        source: '',
        sourceField: '',
        dynaFieldsOrder: this.nextOrderStart + index,
        details: [],
      };
      return this.documentDynamicsService.createDynaFields(request).pipe(
        tap((saved: DynaFields) => this.dacRepository.addField(saved)),
        catchError((): Observable<null> => of(null))
      );
    });

    this.loading = true;
    forkJoin(calls)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(() => {
        this.visible = false;
        this.rows = [];
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('dynamicDocument.createdField'),
        });
        this.completed.emit();
      });
  }

  protected dismiss(): void {
    this.visible = false;
    this.rows = [];
    this.suspicious = [];
  }
}
